// src/GameOverModal.js
import React, { useState, useEffect, useCallback } from 'react';
import './GameOverModal.css';
import StatsPlot from './StatsPlot';
import PuzzleLLMPanel from './PuzzleLLMPanel';

// Inline mini-dial for puzzle controls. Mirrors MainMenu's ArrowSelector
// without depending on it (avoids the circular import + simpler styling).
const Dial = ({ label, value, onPrev, onNext, min, max }) => (
    <div className="puzzle-dial">
        <button
            type="button"
            className="puzzle-dial__btn"
            onClick={onPrev}
            disabled={value <= min}
            aria-label={`Decrease ${label}`}
        >‹</button>
        <span className="puzzle-dial__label">
            {label}: <strong>{value}</strong>
            <span className="puzzle-dial__range">{min}–{max}</span>
        </span>
        <button
            type="button"
            className="puzzle-dial__btn"
            onClick={onNext}
            disabled={value >= max}
            aria-label={`Increase ${label}`}
        >›</button>
    </div>
);

const GameOverModal = ({
    winner,
    historyLength = 0,
    treeNodeCount = 0,
    stats = null,
    onReview,
    onNewGame,
    onMainMenu,
    onClose,
    puzzleMode = false,
    puzzleSolved = false,
    puzzleLabel = null,
    puzzleControls = null,
    onPuzzleControlsChange = null,
    // Watch-an-LLM puzzles: { provider, model, stats, providers, keys,
    // onChangeModel(provider, model), onRetry }. null for manual puzzles.
    puzzleLLM = null,
}) => {
    const updateControl = (k, delta, min, max) => {
        if (!onPuzzleControlsChange || !puzzleControls) return;
        const next = Math.max(min, Math.min(max, (puzzleControls[k] ?? 0) + delta));
        onPuzzleControlsChange({ ...puzzleControls, [k]: next });
    };
    const [selectedIndex, setSelectedIndex] = useState(0); // Default to "Review Game"
    const [acceptInput, setAcceptInput] = useState(false);

    const buttons = puzzleMode
        ? [
              // Review = dismiss the modal so the user can pan/inspect the
              // post-move board. Reopen via the hamburger pause menu, which
              // mirrors the same verdict + dials + New Puzzle / Main Menu.
              { label: 'Review', action: onReview, className: 'review-btn' },
              ...(puzzleLLM?.onRetry ? [{ label: 'Retry Puzzle', action: puzzleLLM.onRetry, className: 'retry-btn' }] : []),
              { label: 'New Puzzle', action: onNewGame, className: 'new-game-btn' },
              ...(onMainMenu ? [{ label: 'Main Menu', action: onMainMenu, className: 'main-menu-btn' }] : []),
          ]
        : [
              { label: 'Review Game', action: onReview, className: 'review-btn' },
              { label: 'New Game', action: onNewGame, className: 'new-game-btn' },
              ...(onMainMenu ? [{ label: 'Main Menu', action: onMainMenu, className: 'main-menu-btn' }] : []),
          ];

    const handleKeyDown = useCallback((e) => {
        if (!winner || winner === 0) return;

        if (e.key === 'ArrowLeft' || e.key === 'a' || e.key === 'A') {
            setSelectedIndex(i => Math.max(0, i - 1));
            e.preventDefault();
        } else if (e.key === 'ArrowRight' || e.key === 'd' || e.key === 'D') {
            setSelectedIndex(i => Math.min(buttons.length - 1, i + 1));
            e.preventDefault();
        } else if (e.key === 'Enter') {
            if (!acceptInput) {
                e.preventDefault();
                return;
            }
            const btn = buttons[selectedIndex];
            if (btn?.action) btn.action();
            e.preventDefault();
        } else if (e.key === 'Escape') {
            onClose?.();
            e.preventDefault();
        }
    }, [winner, buttons, selectedIndex, onClose, acceptInput]);

    useEffect(() => {
        if (!winner || winner === 0) return;

        window.addEventListener('keydown', handleKeyDown, true);
        return () => window.removeEventListener('keydown', handleKeyDown, true);
    }, [winner, handleKeyDown]);

    // Reset selection when modal opens, with a short delay before accepting Enter
    // to prevent the winning move's Enter keypress from immediately triggering a button
    useEffect(() => {
        if (winner && winner !== 0) {
            setSelectedIndex(0); // Default to "Review Game"
            setAcceptInput(false);
            const timer = setTimeout(() => setAcceptInput(true), 400);
            return () => clearTimeout(timer);
        }
    }, [winner]);

    if (!winner || winner === 0) return null;

    const winnerText = winner === 1 ? 'PLAYER 1' : 'PLAYER 2';
    const winnerClass = winner === 1 ? 'player1' : 'player2';

    // Calculate some basic stats
    const totalTurns = Math.max(0, historyLength - 1); // Linear history = turns
    const totalMoves = Math.max(0, treeNodeCount - 1); // Tree nodes = moves

    // Extract stats if provided
    const p1Stats = stats?.p1 || {};
    const p2Stats = stats?.p2 || {};
    const possession = stats?.possession || null;
    const pieceTilt = stats?.pieceTilt || null;
    const statsHistory = stats?.statsHistory || [];

    const hasJumpStats = (p1Stats.avgJumpsPerSeq !== undefined || p2Stats.avgJumpsPerSeq !== undefined);
    const hasPossession = possession !== null;
    const hasPieceTilt = pieceTilt !== null;
    const hasStatsHistory = statsHistory.length >= 2;

    return (
        <div className="game-over-overlay" onClick={onClose}>
            <div className="game-over-modal" onClick={e => e.stopPropagation()}>
                <div className="game-over-content">
                    <div className="game-over-announcement">
                        {puzzleMode ? (
                            <>
                                <span className={`winner-label ${winnerClass}`}>PUZZLE</span>
                                <span className="wins-text">{puzzleSolved ? 'SOLVED' : 'FAILED'}</span>
                            </>
                        ) : (
                            <>
                                <span className={`winner-label ${winnerClass}`}>{winnerText}</span>
                                <span className="wins-text">WINS</span>
                            </>
                        )}
                    </div>
                    {puzzleMode && puzzleLabel && (
                        <div className="puzzle-verdict">{puzzleLabel}</div>
                    )}

                    {puzzleMode && !puzzleLLM && (
                        <div className="puzzle-llm__hint puzzle-llm__hint--solo">
                            Next attempt is played by you. To have a model solve, start from the main menu in Watch an LLM mode.
                        </div>
                    )}

                    {puzzleMode && puzzleLLM && (
                        <PuzzleLLMPanel puzzleLLM={puzzleLLM} />
                    )}

                    {!puzzleMode && (
                    <div className="game-over-stats">
                        <div className="stat-item">
                            <span className="stat-value">{totalTurns}</span>
                            <span className="stat-label">turns</span>
                        </div>
                        <div className="stat-item">
                            <span className="stat-value">{totalMoves}</span>
                            <span className="stat-label">moves</span>
                        </div>
                    </div>
                    )}

                    {!puzzleMode && (hasJumpStats || hasPossession || hasPieceTilt || hasStatsHistory) && (
                        <div className="game-over-detailed-stats">
                            {hasJumpStats && (
                                <div className="stats-comparison">
                                    <h4 className="stats-section-title">JUMP SEQUENCES</h4>
                                    <div className="stats-row stats-header">
                                        <span className="stats-label"></span>
                                        <span className="stats-p1-label">P1</span>
                                        <span className="stats-divider"></span>
                                        <span className="stats-p2-label">P2</span>
                                    </div>
                                    <div className="stats-row">
                                        <span className="stats-label">Avg jumps/seq</span>
                                        <span className="stats-p1">{p1Stats.avgJumpsPerSeq?.toFixed(1) ?? '-'}</span>
                                        <span className="stats-divider">|</span>
                                        <span className="stats-p2">{p2Stats.avgJumpsPerSeq?.toFixed(1) ?? '-'}</span>
                                    </div>
                                    <div className="stats-row">
                                        <span className="stats-label">Avg pieces/jump</span>
                                        <span className="stats-p1">{p1Stats.avgPiecesPerJump?.toFixed(1) ?? '-'}</span>
                                        <span className="stats-divider">|</span>
                                        <span className="stats-p2">{p2Stats.avgPiecesPerJump?.toFixed(1) ?? '-'}</span>
                                    </div>
                                </div>
                            )}

                            {/* Stats plot showing field position and piece tilt over time */}
                            {hasStatsHistory && (
                                <div className="game-over-stats-plot">
                                    <h4 className="stats-section-title">OVER TIME</h4>
                                    <StatsPlot statsHistory={statsHistory} />
                                </div>
                            )}

                            {/* Fallback bars when no time-series data available */}
                            {!hasStatsHistory && hasPossession && (
                                <div className="possession-stats">
                                    <h4 className="stats-section-title">FIELD POSITION</h4>
                                    <div className="possession-bar-container">
                                        <div
                                            className="possession-bar p1-possession"
                                            style={{ width: `${possession.p1}%` }}
                                        />
                                        <div
                                            className="possession-bar p2-possession"
                                            style={{ width: `${possession.p2}%` }}
                                        />
                                    </div>
                                    <div className="possession-labels">
                                        <span className="possession-p1">{possession.p1}% P1</span>
                                        <span className="possession-p2">P2 {possession.p2}%</span>
                                    </div>
                                </div>
                            )}

                            {!hasStatsHistory && hasPieceTilt && (
                                <div className="possession-stats">
                                    <h4 className="stats-section-title">PIECE TILT</h4>
                                    <div className="possession-bar-container">
                                        <div
                                            className="possession-bar p1-possession"
                                            style={{ width: `${pieceTilt.p1}%` }}
                                        />
                                        <div
                                            className="possession-bar p2-possession"
                                            style={{ width: `${pieceTilt.p2}%` }}
                                        />
                                    </div>
                                    <div className="possession-labels">
                                        <span className="possession-p1">{pieceTilt.p1}% P1</span>
                                        <span className="possession-p2">P2 {pieceTilt.p2}%</span>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {puzzleMode && puzzleControls && onPuzzleControlsChange && (
                        <div className="puzzle-controls">
                            {(() => {
                                const types = [
                                    { id: 'win',         label: 'Win',         min: 1, max: 8 },
                                    { id: 'threat',      label: 'Threat',      min: 2, max: 6 },
                                    { id: 'passthrough', label: 'Passthrough', min: 2, max: 8 },
                                    { id: 'denial',      label: 'Denial',      min: 4, max: 9 },
                                ];
                                const setType = (id) => {
                                    const meta = types.find(t => t.id === id);
                                    const clamped = Math.max(meta?.min ?? 1, Math.min(meta?.max ?? 4, puzzleControls.num_jumps));
                                    onPuzzleControlsChange({
                                        ...puzzleControls,
                                        puzzle_type: id,
                                        num_jumps: clamped,
                                    });
                                };
                                return (
                                    <div className="puzzle-controls__type">
                                        {types.map(t => (
                                            <button
                                                key={t.id}
                                                type="button"
                                                className={`puzzle-dial__btn ${puzzleControls.puzzle_type === t.id ? 'active' : ''}`}
                                                onClick={() => setType(t.id)}
                                            >{t.label}</button>
                                        ))}
                                    </div>
                                );
                            })()}
                            {(() => {
                                const t = puzzleControls.puzzle_type;
                                const meta = ({
                                    win:         { min: 1, max: 8 },
                                    threat:      { min: 2, max: 6 },
                                    passthrough: { min: 2, max: 8 },
                                    denial:      { min: 4, max: 9 },
                                })[t] || { min: 1, max: 4 };
                                const dialLabel = t === 'threat'      ? 'Threat depth (jumps)'
                                                : t === 'denial'    ? 'Max total jumps'
                                                : t === 'passthrough' ? 'Jumps in sequence'
                                                : 'Jumps to win';
                                return (
                                    <Dial
                                        label={dialLabel}
                                        value={Math.max(meta.min, Math.min(meta.max, puzzleControls.num_jumps))}
                                        onPrev={() => updateControl('num_jumps', -1, meta.min, meta.max)}
                                        onNext={() => updateControl('num_jumps', +1, meta.min, meta.max)}
                                        min={meta.min}
                                        max={meta.max}
                                    />
                                );
                            })()}
                            {puzzleControls.puzzle_type === 'win' && (
                                <Dial
                                    label="Max noise stones"
                                    value={puzzleControls.noise}
                                    onPrev={() => updateControl('noise', -1, 0, 20)}
                                    onNext={() => updateControl('noise', +1, 0, 20)}
                                    min={0}
                                    max={20}
                                />
                            )}
                            <Dial
                                label="Max stones per jump"
                                value={puzzleControls.max_jump_len}
                                onPrev={() => updateControl('max_jump_len', -1, 1, 8)}
                                onNext={() => updateControl('max_jump_len', +1, 1, 8)}
                                min={1}
                                max={8}
                            />
                            {(() => {
                                // For threat puzzles, the dial reads as "Player facing threat" — the
                                // responder. We display the responder, but App.js's puzzleControls.player
                                // stores the offensive (threat) player, so invert at the boundary.
                                const isThreat = puzzleControls.puzzle_type === 'threat';
                                const displayed = isThreat ? (3 - puzzleControls.player) : puzzleControls.player;
                                const setDisplayed = (p) => onPuzzleControlsChange({
                                    ...puzzleControls,
                                    player: isThreat ? (3 - p) : p,
                                });
                                return (
                                    <>
                                        <div className="puzzle-controls__role">
                                            {isThreat ? 'Player facing threat' : 'Player to move'}
                                        </div>
                                        <div className="puzzle-controls__player">
                                            <button
                                                type="button"
                                                className={`puzzle-dial__btn ${displayed === 1 ? 'active' : ''}`}
                                                onClick={() => setDisplayed(1)}
                                            >P1 (top)</button>
                                            <button
                                                type="button"
                                                className={`puzzle-dial__btn ${displayed === 2 ? 'active' : ''}`}
                                                onClick={() => setDisplayed(2)}
                                            >P2 (bottom)</button>
                                        </div>
                                    </>
                                );
                            })()}
                        </div>
                    )}

                    <div className="game-over-actions">
                        {buttons.map((btn, i) => (
                            <button
                                key={btn.label}
                                className={`game-over-btn ${btn.className} ${i === selectedIndex ? 'selected' : ''}`}
                                onClick={(e) => { btn.action(); e.currentTarget.blur(); }}
                                onMouseEnter={() => setSelectedIndex(i)}
                            >
                                {btn.label}
                            </button>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default GameOverModal;
